import { useEffect } from 'react'
import { useLocation, useRouteError } from 'react-router-dom'

const RELOAD_GUARD_PREFIX = 'fleetops:chunk-reload-attempted:'

// Cada deploy do frontend gera nomes de ficheiro novos para os chunks
// (hash de conteúdo). Um separador aberto antes do deploy fica com
// referências antigas em memória — o próximo `import()` de uma rota lazy
// falha com este erro. A única forma de recuperar é um reload completo
// (busca o index.html novo, que aponta para os chunks corretos).
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported module/i.test(error.message)
}

export function RouteErrorBoundary() {
  const error = useRouteError()
  const location = useLocation()
  const chunkError = isChunkLoadError(error)

  useEffect(() => {
    if (!chunkError) return
    const key = RELOAD_GUARD_PREFIX + location.pathname
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
    window.location.reload()
  }, [chunkError, location.pathname])

  if (chunkError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-sm text-slate-500">A atualizar a aplicação…</p>
      </div>
    )
  }

  const message = error instanceof Error ? error.message : 'Erro inesperado.'
  return (
    <div style={{
      display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      background: '#f8fafc', padding: '24px',
    }}>
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, background: '#fee2e2',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <svg width="24" height="24" fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>
          Algo correu mal
        </h1>
        <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6 }}>
          Ocorreu um erro inesperado. Tente recarregar a página.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Recarregar página
        </button>
        {import.meta.env.DEV && (
          <pre style={{
            marginTop: 24, padding: 12, background: '#1e293b', color: '#f87171',
            borderRadius: 8, fontSize: 11, textAlign: 'left', overflowX: 'auto',
          }}>
            {message}
          </pre>
        )}
      </div>
    </div>
  )
}
